import { useCallback, useRef, useState } from "react";
// ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 顶部导航版式的下拉定位
import { useTopnavDropdown } from "./useTopnavDropdown";
// ═ COMPANY-STRIP END ═
import { ChevronDown, LogOut, KeyRound, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { ROLE_LABEL } from "@/services/types/auth";
import { logout as logoutApi, changePassword } from "@/services/auth";

/**
 * 右上角账号菜单（头像 + 修改密码 + 退出登录）——原来写在 Topbar.tsx 里，THEME-1 抽出来给「侧栏」和「顶部模块导航」两套版式共用，逻辑一字未改。
 */
export function UserMenu() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const handleLogout = async () => {
    await logoutApi();
    navigate("/login", { replace: true });
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  // ═ COMPANY-STRIP BEGIN ═
  const drop = useTopnavDropdown(anchorRef, menuOpen, closeMenu);   // SAAS-2b · 顶部导航版式 fixed 定位；侧栏版式原样
  // ═ COMPANY-STRIP END ═
  // ═ COMPANY-ONLY BEGIN ═
  // const drop: { cls: string; style?: undefined } = { cls: "absolute right-0 top-12" };   // 左侧栏版式：下拉照原样 absolute
  // ═ COMPANY-ONLY END ═
  // 修改密码 modal（2026-07-18 自助改密补缺）
  const [pwOpen, setPwOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const submitPw = async () => {
    setPwMsg(null);
    if (newPw !== newPw2) { setPwMsg({ ok: false, text: "两次输入的新密码不一致" }); return; }
    if (newPw === oldPw) { setPwMsg({ ok: false, text: "新密码不能与当前密码相同" }); return; }
    setPwLoading(true);
    try {
      await changePassword(oldPw, newPw);
      setPwMsg({ ok: true, text: "修改成功 ✅ 下次登录请用新密码" });
      setOldPw(""); setNewPw(""); setNewPw2("");
      setTimeout(() => { setPwOpen(false); setPwMsg(null); }, 1600);
    } catch (e) {
      setPwMsg({ ok: false, text: e instanceof Error && e.message ? e.message : "修改失败，请重试" });
    } finally {
      setPwLoading(false);
    }
  };

  // RBACROLE-1 · 只带自定义角色的人（custom_xxxxxx）显示角色名；其余照旧
  const codes = user?.roleCodes ?? [];
  const customOnly = codes.length > 0 && codes.every((c) => String(c).startsWith("custom_"));
  const roleText = user
    ? customOnly
      ? codes.map((c) => user.roleLabels?.[c] ?? String(c)).join("、")
      : ROLE_LABEL[user.role]
    : "";

  if (!user) return null;
  return (
    <>
      <div className="relative" ref={anchorRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-2.5 rounded-lg px-1 py-1 hover:bg-slate-100"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-[13px] font-bold text-white">
            {user.initial}
          </div>
          <div className="hidden flex-col items-start sm:flex">
            <span className="text-[13px] font-semibold leading-tight text-slate-900">
              {user.name}
            </span>
            <span className="text-[11px] leading-tight text-slate-500">
              {roleText}
            </span>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
        </button>
        {menuOpen && (
          <div className={`${drop.cls} z-30 w-44 rounded-lg border border-slate-200 bg-white p-1 shadow-lg`.trim()} style={drop.style}>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setPwOpen(true);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-100"
            >
              <KeyRound className="h-3.5 w-3.5" strokeWidth={2} />
              修改密码
            </button>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                void handleLogout();
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[13px] text-rose-600 hover:bg-rose-50"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={2} />
              退出登录
            </button>
          </div>
        )}
      </div>

      {/* 修改密码 modal（2026-07-18 自助改密补缺） */}
      {pwOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setPwOpen(false)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">修改密码</h3>
              <button onClick={() => setPwOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">当前密码</span>
                <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoComplete="current-password" className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">新密码</span>
                <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">确认新密码</span>
                <input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} autoComplete="new-password" className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              </label>
              {pwMsg && (
                <p className={`rounded-md px-3 py-2 text-sm font-medium ${pwMsg.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{pwMsg.text}</p>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setPwOpen(false)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">取消</button>
              <button
                onClick={() => void submitPw()}
                disabled={pwLoading || !oldPw || !newPw || !newPw2}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {pwLoading ? "提交中…" : "确认修改"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
